/* Zeltron cable gift: one gift, matched to the strongest eligible charger/bundle. */
(() => {
  'use strict';

  const offer = window.zeltronFreeCableOffer;
  if (!offer || !offer.giftVariantIds) return;

  const giftIds = offer.giftVariantIds;
  const giftVariantIds = new Set(Object.values(giftIds).map(Number));
  const unfundedGiftIds = new Set();
  const propertyName = '_Zeltron free cable offer';
  const sections = [
    'cart-notification-product', 'cart-notification-subtotal',
    'cart-notification-count', 'cart-notification-discount'
  ];
  const nativeFetch = window.fetch.bind(window);
  let lastCart = null;
  let inFlight = false;
  let pending = false;
  let timer;

  // Bundle handles start with their charger model (Z45, ZM65, etc.). Prefer
  // that number to the cable wattage also printed in some bundle titles.
  const chargerWattage = ({ title = '', handle = '' }) => {
    const bundleModel = String(handle).match(/^z(?:ivo|m)?(140|100|70|65|45|30|25)(?:\b|-)/i);
    const titleModel = String(title).match(/\b(?:ZIVO|ZM|Z)(140|100|70|65|45|30|25)\b/i);
    const ordinaryWatt = String(title).match(/\b(140|100|70|65|45|30|25)\s*W\b/i);
    return Number(bundleModel?.[1] || titleModel?.[1] || ordinaryWatt?.[1] || 0);
  };

  const giftForWattage = (watts) =>
    watts >= 140 ? Number(giftIds[240]) :
    watts >= 45 ? Number(giftIds[100]) :
    watts >= 25 ? Number(giftIds[60]) : 0;
  const giftRank = (id) => id === Number(giftIds[240]) ? 3 :
    id === Number(giftIds[100]) ? 2 : id === Number(giftIds[60]) ? 1 : 0;

  const eligible = new Map();
  for (const product of offer.eligibleVariants || []) {
    const giftId = giftForWattage(chargerWattage(product));
    if (giftId) eligible.set(Number(product.id), giftId);
  }

  const isGift = (item) =>
    giftVariantIds.has(Number(item.variant_id)) &&
    String(item.properties?.[propertyName] || '').toLowerCase() === 'true';

  const desiredGift = (items, newVariantId) => {
    let desired = eligible.get(Number(newVariantId)) || 0;
    for (const item of items || []) {
      if (isGift(item)) continue;
      const candidate = eligible.get(Number(item.variant_id)) || 0;
      if (giftRank(candidate) > giftRank(desired)) desired = candidate;
    }
    return desired;
  };

  const giftItem = (id) => ({
    id, quantity: 1,
    properties: {
      [propertyName]: 'true',
      '_Offer message': offer.message || 'Free cable with charger or bundle purchase'
    }
  });

  // A theme cannot make a line free. Only show a gift if Shopify's discount
  // actually reduced that exact marked line to zero at the server.
  const addedGift = (result, id) => (result.items || []).find((item) =>
    Number(item.variant_id) === Number(id) && isGift(item));
  const isFree = (item) => item && Number(item.final_line_price) === 0;

  const loadCart = () => nativeFetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    credentials: 'same-origin', cache: 'no-store'
  }).then((response) => {
    if (!response.ok) throw new Error('Could not load cart');
    return response.json();
  }).then((cart) => { lastCart = cart; return cart; });

  const updateCart = (path, data) => nativeFetch(`${window.Shopify?.routes?.root || '/'}cart/${path}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ ...data, sections, sections_url: window.location.pathname })
  }).then(async (response) => {
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Free cable cart update failed');
    return result;
  });

  const refreshDrawer = (cart) => {
    const drawer = document.querySelector('cart-notification');
    if (drawer && cart.sections && typeof drawer.renderContents === 'function') {
      drawer.renderContents(cart);
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zeltron-free-cable-offer', cartData: cart });
    }
  };

  const sync = async () => {
    if (inFlight) { pending = true; return; }
    inFlight = true;
    try {
      let cart = await loadCart();
      const desired = desiredGift(cart.items);
      const marked = cart.items.filter(isGift);
      const keep = marked.find((item) => Number(item.variant_id) === desired);
      const extras = marked.filter((item) => item !== keep);
      if (extras.length) {
        const updates = {};
        extras.forEach((item) => { updates[item.key] = 0; });
        cart = await updateCart('update', { updates });
        lastCart = cart;
        refreshDrawer(cart);
      }
      if (desired && !keep && !unfundedGiftIds.has(desired)) {
        cart = await updateCart('add', { items: [giftItem(desired)] });
        if (!isFree(addedGift(cart, desired))) {
          unfundedGiftIds.add(desired);
          const latest = await loadCart();
          const charged = latest.items.find((item) =>
            isGift(item) && Number(item.variant_id) === desired);
          if (charged) await updateCart('change', { id: charged.key, quantity: 0 });
          return;
        }
        // add.js can return only added items; read the full count for the drawer.
        const fresh = await loadCart();
        cart.item_count = fresh.item_count;
        refreshDrawer(cart);
      }
    } catch (error) {
      console.warn('Zeltron free cable could not sync.', error);
    } finally {
      inFlight = false;
      if (pending) { pending = false; scheduleSync(0); }
    }
  };

  const scheduleSync = (delay = 0) => {
    clearTimeout(timer);
    timer = setTimeout(sync, delay);
  };

  // Product, home and collection forms enter the product and gift together.
  offer.addWithGift = async (formData, config) => {
    const variantId = Number(formData.get('id'));
    let cart = lastCart;
    if (eligible.has(variantId) && !cart) {
      try { cart = await loadCart(); } catch (_) { /* sync will retry */ }
    }
    const desired = desiredGift(cart?.items || [], variantId);
    const alreadyGifted = cart?.items?.some((item) =>
      isGift(item) && Number(item.variant_id) === desired
    );
    const includeGift = eligible.has(variantId) && desired &&
      !alreadyGifted && !unfundedGiftIds.has(desired);
    let response;

    if (includeGift) {
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      }
      const primary = {
        id: variantId,
        quantity: Math.max(1, Number(formData.get('quantity')) || 1)
      };
      if (Object.keys(properties).length) primary.properties = properties;
      if (formData.get('selling_plan')) primary.selling_plan = formData.get('selling_plan');
      response = await nativeFetch(`${routes.cart_add_url}`, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [primary, giftItem(desired)],
          sections: formData.getAll('sections').flatMap((entry) => String(entry).split(',')),
          sections_url: formData.get('sections_url') || window.location.pathname
        })
      });
    } else {
      response = await nativeFetch(`${routes.cart_add_url}`, config);
    }

    let result = await response.json();
    if (response.ok) {
      if (includeGift && !isFree(addedGift(result, desired))) {
        unfundedGiftIds.add(desired);
        const latest = await loadCart();
        const charged = latest.items.find((item) =>
          isGift(item) && Number(item.variant_id) === desired);
        if (charged) result = await updateCart('change', { id: charged.key, quantity: 0 });
      }
      if (includeGift && result.item_count == null) {
        result.item_count = (cart?.item_count || 0) +
          Math.max(1, Number(formData.get('quantity')) || 1) + 1;
      }
      // Reconcile another open tab, quantity changes, or an older gift.
      scheduleSync(0);
    }
    return result;
  };

  const watchCartUpdates = () => {
    if (window.__zeltronFreeCableOfferFetchWatcher) return;
    window.__zeltronFreeCableOfferFetchWatcher = true;
    window.fetch = (...args) => {
      const request = args[0];
      const url = typeof request === 'string' ? request : request?.url;
      let mutation = false;
      try {
        mutation = /\/cart\/(add|change|update)(\.js)?$/.test(new URL(url || '', location.origin).pathname);
      } catch (_) { /* ignore malformed URL */ }
      const promise = nativeFetch(...args);
      if (mutation) promise.then((response) => {
        if (response.ok) scheduleSync(50);
      }).catch(() => {});
      return promise;
    };
  };

  const start = () => {
    watchCartUpdates();
    scheduleSync(0);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source } = {}) => {
        if (source !== 'zeltron-free-cable-offer') scheduleSync(0);
      });
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else start();
})();
